import React, { useEffect, useRef } from 'react';
import { Text, Platform } from 'react-native';
import useTooltipStore from '@/data/store/useTooltipStore';
import { impactLight } from '@/utils/haptic';
import usePlatform from '@/utils/hooks/usePlatform';
import Unicon from '@/components/General/Unicon';
import { TooltipOffsetContext } from '@/components/General/Tooltip';

// Detect touch-only web devices (no hover) once. On such devices the browser
// synthesizes mouseenter/mouseleave from taps, which opens the tooltip then
// closes it ~150ms later (the "shows for a split second" flicker). On
// touch-only web we skip the hover listeners and rely on the press/long-press
// path with an auto-close timeout, mirroring Tooltip.jsx's behavior.
const isWebTouchOnly = (() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
})();

/**
 * A help icon that renders inline when nested inside a <Text> element.
 * Unlike the Tooltip+Pressable pattern, this can be placed inside a wrapping
 * <Text> so the icon stays on the same line as the surrounding text.
 *
 * On web (hover-capable): shows tooltip on hover (native DOM mouseenter/mouseleave) and click.
 * On web (touch-only): shows tooltip on press/long press with an auto-close timeout.
 * On native: shows tooltip on press and long press (with haptic).
 */
const InlineHelpIcon = ({ text, size = 18, colorVariant = 'textSecondary', name = 'help-circle' }) => {
    // Subscribe only to the stable open/close actions so each mounted icon
    // never re-renders on store writes; transient state (isOpen/activeId) is
    // read via getState() inside event handlers.
    const open = useTooltipStore(s => s.open);
    const close = useTooltipStore(s => s.close);
    const { isNative } = usePlatform();
    const id = React.useId();
    const ref = useRef(null);
    const closeTimer = useRef(null);
    // Same correction Tooltip applies: inside native sheets, pageY is relative
    // to the modal's root — the root-rendered tooltip needs screen coords.
    const modalYOffset = React.useContext(TooltipOffsetContext);

    const openTooltip = (x, y) => {
        if (!text) return;
        open(x, y, text, '', id);
    };

    const scheduleClose = (delay = 200) => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => {
            // Use getState() — the store snapshot at render time is stale
            // inside this closure and won't reflect the current activeId.
            if (useTooltipStore.getState().activeId === id) close();
        }, delay);
    };

    const show = (e) => {
        const state = useTooltipStore.getState();
        if (!text || (state.isOpen && state.activeId === id)) {
            close();
            return;
        }
        if (isNative) impactLight();
        const { pageX, pageY } = e.nativeEvent;
        openTooltip(pageX, pageY + modalYOffset);
        // Native and touch-only web: auto-close after a read-time-based delay
        // so the tooltip doesn't depend on a hover-out event that never comes.
        if (isNative || isWebTouchOnly) {
            const timeToClose = Math.max(text.split(' ').length * 250, 1500);
            scheduleClose(timeToClose);
        }
    };

    // Web: attach native DOM hover listeners — RN Web Text doesn't reliably
    // fire onHoverIn/onHoverOut, so use the underlying DOM node directly.
    // Skipped on touch-only web (synthesized hover causes open/close flicker);
    // there the press/long-press path with auto-close handles it.
    useEffect(() => {
        if (Platform.OS !== 'web' || isWebTouchOnly || !ref.current) return;
        const el = ref.current;
        const handleEnter = (e) => {
            if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
            // If a different tooltip is mid-close, force-close it so this one opens immediately.
            const state = useTooltipStore.getState();
            if (state.isOpen && state.activeId !== id) close();
            openTooltip(e.clientX, e.clientY);
        };
        const handleLeave = () => scheduleClose(150);
        const handleClick = (e) => {
            e.stopPropagation();
            const state = useTooltipStore.getState();
            if (state.isOpen && state.activeId === id) {
                close();
            } else {
                openTooltip(e.clientX, e.clientY);
            }
        };
        el.addEventListener('mouseenter', handleEnter);
        el.addEventListener('mouseleave', handleLeave);
        el.addEventListener('click', handleClick);
        el.style.cursor = 'help';
        return () => {
            el.removeEventListener('mouseenter', handleEnter);
            el.removeEventListener('mouseleave', handleLeave);
            el.removeEventListener('click', handleClick);
            if (closeTimer.current) clearTimeout(closeTimer.current);
        };
    }, [text, id]);

    useEffect(() => () => { close(); if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

    return (
        <Text
            ref={ref}
            onPress={show}
            onLongPress={show}
            style={{ includeFontPadding: false, textAlignVertical: 'center', lineHeight: size + 2 }}
            suppressHighlighting
        >
            {' '}
            <Unicon name={name} size={size} colorVariant={colorVariant} style={{ marginTop: 5, marginBottom: -3 }} />
        </Text>
    );
};

export default InlineHelpIcon;
